import { Style } from 'inlines'
import { useClient, useQuery } from '@based/sdk/react'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { faPlus, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Card } from '@app/components/blocks/card/Card'
import { AvatarStack } from '@app/components/blocks/avatarStack/AvatarStack'
import Column from '@app/components/layout/Column'
import { useState } from 'react'
import Row from '@app/components/layout/Row'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { useTranslations } from '@i18n/useLanguage'
import useMediaQuery from '@app/util/useMediaQuery'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { ServiceProviderConceptViewModel } from 'functions/app/service-provider-concept/view'

export interface ServiceProviderConceptPageHeaderCardProps {
  serviceProviderConcept: ServiceProviderConceptViewModel
  style?: Style
}

export const ServiceProviderConceptPageHeaderCard = ({
  serviceProviderConcept,
  style,
}: ServiceProviderConceptPageHeaderCardProps) => {
  const client = useClient()
  const { user } = useUserContext()
  const t = useTranslations().app
  const isMobile = useMediaQuery()
  const { withAuthCheck } = useAuthModal()
  const [shareModalOpen, setShareModalOpen] = useState(false)

  const isFollowingQuery = useQuery(user?.id ? 'is-following' : null, {
    entityType: 'serviceProviderConcept',
    entityId: serviceProviderConcept.id,
    userId: user?.id,
  })
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'serviceProviderConcept',
    entityIds: [serviceProviderConcept.id],
  })
  const isFollowing = (isFollowingQuery.data as boolean) ?? false

  const toggleFollow = () => {
    void withAuthCheck(async () => {
      await client.call('follow-service-provider-concept', {
        id: serviceProviderConcept.id,
      })
    })
  }

  return (
    <Card
      image={serviceProviderConcept?.banner?.src}
      imageAlt={`${serviceProviderConcept.name} ${t.common.banner}`}
      imageStyle={{
        height: '18rem',
      }}
      padded={false}
      border={true}
      style={{
        width: '100%',
        ...style,
      }}
    >
      <Column
        gap="1rem"
        style={{
          flex: '1 0 0',
          width: '100%',
        }}
      >
        <Column
          gap="0.5rem"
          style={{
            width: '100%',
          }}
        >
          <Row
            style={{ width: '100%', flexWrap: isMobile ? 'wrap' : 'nowrap' }}
          >
            <Title.Page styledAsLevel={2} style={{ margin: 0 }}>
              {serviceProviderConcept.name}
            </Title.Page>
            <Row style={{ marginLeft: isMobile ? '0' : 'auto' }}>
              <Button
                variant="secondary"
                label={t.common.share}
                onClick={() => setShareModalOpen(true)}
              />
              <Button
                variant="primary"
                label={isFollowing ? t.common.following : t.common.follow}
                icon={isFollowing ? faCheck : faPlus}
                iconPosition="left"
                onClick={toggleFollow}
              />
            </Row>
          </Row>
        </Column>

        {/* Followers */}
        {serviceProviderConcept?.followers?.length > 0 && (
          <Row gap="0.5rem" style={{ alignItems: 'center' }}>
            <AvatarStack
              style={{ marginRight: '1rem' }}
              size="extra-small"
              avatars={serviceProviderConcept.followers}
              total={followerCountQuery.data?.[0] ?? 0}
              version="dark"
            />

            <Text.Small
              style={{
                color: 'var(--palette-blue-500)',
              }}
            >
              {followerCountQuery.data?.[0] ?? 0} {t.common.followers}
            </Text.Small>
          </Row>
        )}
      </Column>

      <SocialShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={serviceProviderConcept?.name}
      />
    </Card>
  )
}
